import { useState, MouseEvent } from 'react';
import MoreVert from '@mui/icons-material/MoreVert';
import {
  Button,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  Typography,
} from '@mui/material';
import {
  AccessStatus,
  getStatus,
  getStatusLabel,
  UserStatus,
} from '@src/constants/UserStatus';
import theme from '@src/theme';
import AccessRequestModal from './AccessRequestModal';
import HospitalUserDTO from '@src/adapters/dto/hospital/HospitalUserDTO';
import useToggleHospitalUserLazy from '@src/adapters/hooks/hospital/UseToggleHospitalUser';
import { useSnackbar } from 'notistack';
import { generatePath, Link } from 'react-router-dom';
import { Routes } from '@src/navigation/Routes';
import { StatusUpdate } from '@src/constants/StatusUpdate';
import { useNavigationStore } from '@src/adapters/stores/useNavigationStore';

interface StatusProps {
  user: HospitalUserDTO;
  onStatusChange?: (userId: string, status: StatusUpdate) => void;
  isRequestedAccess: boolean;
}

interface StatusMenuItem {
  key: string;
  label: string;
  onClick: () => void;
  to?: string;
}

function StatusTableCell({
  user,
  onStatusChange,
  isRequestedAccess,
}: StatusProps) {
  const { currentHospital, currentOrganisation } = useNavigationStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [modalOpen, setModalOpen] = useState(false);
  const { execute: toggleUserStatus } = useToggleHospitalUserLazy();

  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const getStatusMenuItems = (status: string): StatusMenuItem[] | undefined => {
    switch (status) {
      case `${UserStatus.Active}`:
        return [
          {
            key: 'deactivate',
            label: 'Deactivate',
            onClick: async () => {
              await toggleUserStatus({
                isActive: false,
                userId: user.userId,
                hospitalId: user.hospitalId,
              });

              onStatusChange?.(user.userId, UserStatus.Deactivated);

              enqueueSnackbar(`User ${UserStatus.Deactivated}`, {
                variant: 'success',
              });
            },
          },
          {
            key: 'edit-user',
            label: 'Edit user',
            onClick: () => {},
            to: generatePath(Routes.OrganisationHospitalUserEdit, {
              organisationSlug: currentOrganisation?.name ?? '',
              hospitalSlug: currentHospital?.name ?? '',
              userSlug: `${user.userId}`,
            }),
          },
        ];
      case `${AccessStatus.Invited}`:
        return [
          { key: 'resend-invite', label: 'Resend invite', onClick: () => {} },
          { key: 'cancel-invite', label: 'Cancel invite', onClick: () => {} },
        ];
      case `${UserStatus.Deactivated}`:
        return [
          {
            key: 'activate',
            label: 'Reactivate',
            onClick: async () => {
              await toggleUserStatus({
                isActive: true,
                userId: user.userId,
                hospitalId: user.hospitalId,
              });

              onStatusChange?.(user.userId, UserStatus.Active);
              enqueueSnackbar(`User ${UserStatus.Active}`, {
                variant: 'success',
              });
            },
          },
        ];
    }
  };

  const menuItems = getStatusMenuItems(user.status);

  return (
    <>
      <TableCell>
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}>
          <Typography
            variant="body2"
            color={getStatus(user.status)?.text ?? theme.palette.text.primary}>
            {getStatusLabel(user.status)}
          </Typography>

          {isRequestedAccess ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setModalOpen(true)}
              sx={{
                width: '78px',
                height: '30px',
              }}>
              Respond
            </Button>
          ) : (
            <>
              <IconButton
                id="more-button"
                onClick={(event: MouseEvent<HTMLElement>) =>
                  setAnchorEl(event.currentTarget)
                }>
                <MoreVert />
              </IconButton>
              <Menu
                id="more-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'center', horizontal: 'right' }}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: '0.5rem !important',
                      mt: '1rem !important',
                      ml: '1rem !important',
                    },
                  },
                }}
                MenuListProps={{
                  'aria-labelledby': 'more-button',
                }}>
                {menuItems?.map((item) =>
                  item.to ? (
                    <MenuItem key={item.key} component={Link} to={item.to}>
                      <ListItemText>{item.label}</ListItemText>
                    </MenuItem>
                  ) : (
                    <MenuItem
                      key={item.key}
                      onClick={() => {
                        item.onClick();
                        handleClose();
                      }}>
                      <ListItemText>{item.label}</ListItemText>
                    </MenuItem>
                  )
                )}
              </Menu>
            </>
          )}
        </Stack>
      </TableCell>

      <AccessRequestModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        user={user}
        onStatusChange={onStatusChange}
      />
    </>
  );
}

export default StatusTableCell;
